import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Download, Plus, Loader2 } from 'lucide-react'
import { useClients } from '@/modules/clients/hooks/useClients'
import { ClientsTable } from '@/modules/clients/components/ClientsTable'
import { ErrorState } from '@/shared/components/ui/atoms/ErrorState'
import { Can } from '@/modules/auth/components/Can'
import { Permission } from '@/modules/auth/permissions/permissions'
import { clientService } from '@/modules/clients/services/clientService'
import { branchService } from '@/modules/branches/services/branchService'
import { configService } from '@/modules/config/services/configService'

export function ClientsListPage() {
  const {
    data, total, isLoading, error, filters, hasActiveFilters,
    setPage, setLimit, setSorting, applyFilters, clearFilters, refetch,
  } = useClients()

  const [isExporting, setIsExporting] = useState(false)
  const [branchNames, setBranchNames] = useState<Record<number, string>>({})
  const [categories, setCategories] = useState<string[]>([])

  // Load branch names and categories for filter dropdowns
  useEffect(() => {
    branchService.getAll().then((branches) => {
      const names: Record<number, string> = {}
      branches.forEach((b) => { names[b.id] = `${b.commune} - ${b.address}` })
      setBranchNames(names)
    }).catch(() => {})

    configService.get().then((config) => {
      if (config.categories?.values) {
        setCategories(Object.keys(config.categories.values))
      }
    }).catch(() => {})
  }, [])

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const exportFilters = { ...filters }
      delete (exportFilters as Record<string, unknown>).page
      delete (exportFilters as Record<string, unknown>).limit
      const blob = await clientService.exportAll(exportFilters)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `clientes_${new Date().toISOString().split('T')[0]}.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error exporting:', err)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        <div className="flex gap-2">
          <Can permission={Permission.CLIENTS_EXPORT}>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="btn btn-secondary flex items-center gap-1.5"
            >
              {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {isExporting ? 'Exportando...' : hasActiveFilters ? 'Exportar (filtrado)' : 'Exportar'}
            </button>
          </Can>
          <Link to="/clients/new" className="btn btn-primary flex items-center gap-1.5">
            <Plus size={16} />
            Nuevo Cliente
          </Link>
        </div>
      </div>

      {error && <ErrorState message={error} onRetry={refetch} />}

      <div className="card">
        <ClientsTable
          data={data}
          total={total}
          filters={filters}
          hasActiveFilters={hasActiveFilters}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onApplyFilters={applyFilters}
          onSortingChange={setSorting}
          onClearFilters={clearFilters}
          isLoading={isLoading}
          branchNames={branchNames}
          categories={categories}
        />
      </div>
    </div>
  )
}
